import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { FaHome } from 'react-icons/fa';

const PitchBadgeSelection = () => {
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [userCountry, setUserCountry] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart, setApplicationReference, setUserInfo } = useCart();

  // Constants
  const USD_TO_NGN_RATE = 1690; // Updated exchange rate

  useEffect(() => {
    // Automatically select the badge since there's only one
    setSelectedBadge("PREMIUM ELITE PLUS FILM INDUSTRY BADGE");
    
    // Get parameters from URL query params
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const name = params.get('name');
    const email = params.get('email');
    
    // Store reference code if available
    if (ref) {
      setReferenceCode(ref);
      setApplicationReference(ref);
      sessionStorage.setItem('pitchReferenceCode', ref);
    } else {
      // Check if we have a stored reference code
      const storedRef = sessionStorage.getItem('pitchReferenceCode');
      if (storedRef) {
        setReferenceCode(storedRef);
        setApplicationReference(storedRef);
      }
    }
    
    // Store user info if available
    if (name || email) {
      const userInfo = {
        name: name || '',
        email: email || ''
      };
      setUserInfo(userInfo);
      localStorage.setItem('pitchUserInfo', JSON.stringify(userInfo));
    } else {
      // Check if we have stored user info
      const storedUserInfo = localStorage.getItem('pitchUserInfo');
      if (storedUserInfo) {
        setUserInfo(JSON.parse(storedUserInfo));
      }
    }
    
    // Function to detect user's country
    const detectUserCountry = async () => {
      try {
        // First try to get from localStorage
        const storedCountry = localStorage.getItem('userCountry');
        console.log('Stored country:', storedCountry);
        
        if (storedCountry) {
          setUserCountry(storedCountry);
          return;
        }

        // If not in localStorage, try to get from IP
        console.log('Fetching country from IP...');
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        console.log('IP API response:', data);
        
        if (data.country_name === 'Nigeria') {
          console.log('Setting country to Nigeria');
          setUserCountry('Nigeria');
          localStorage.setItem('userCountry', 'Nigeria');
        }
      } catch (error) {
        console.error('Error detecting country:', error);
      }
    };

    detectUserCountry();
  }, [location, setApplicationReference, setUserInfo]);

  const formatPrice = (price) => {
    console.log('Formatting price for country:', userCountry);
    if (userCountry === 'Nigeria') {
      // Convert USD to NGN (using current rate)
      const nairaPrice = price * USD_TO_NGN_RATE;
      console.log('Converting to Naira:', nairaPrice);
      return `₦${nairaPrice.toLocaleString('en-NG')}`;
    }
    return `$${price}`;
  };

  // Only including the Premium Elite Plus Film Industry Badge
  const pitchBadge = {
    name: "PREMIUM ELITE PLUS FILM INDUSTRY BADGE",
    price: 142.12, // Calculated as 240,177/1690
    details: "(1 BADGE)",
    summary: [
      "• Forum",
      "• Deal Table",
      "• Investor's Room",
      "• Film Finance Market",
      "• Pitch at the Pitch Room",
      "• Investor's Lounge",
      "And More"
    ],
    features: [
      "Access entry to restricted areas and elite events at AFFF",
      "Enjoy exceptional VIP opportunities at AFFF",
      "Enjoy elite red-carpet privileges at AFFF",
      "Gain privileged access to high-stakes negotiations and investment opportunities at AFFF Deal Table",
      "Stand a chance to Present Your Project to AFFF top Tier accredited Financier from private equity, hedge funders, Angel Investors, Premium Bank CEO in the AFFF Deal Room",
      "Get one-on-one consultations with top financial advisors and industry experts to tailor strategies for your unique needs at AFFF Investors Room",
      "Get direct access to AFFF financiers and investors for engaging in confidential negotiations and acquiring strategic investment insights at AFFF Investors Room",
      "Maximize your project's funding potential at AFFF Investors Room",
      "Be invited to contribute to advisory boards that shape future industry developments and investment strategies at AFFF Private Executive Film Investment Roundtable",
      "Establish impactful connections with AFFF sponsors, opening doors to new opportunities and collaborations at AFFF Private Executive Film Investment Roundtable",
      "Participate in an in-depth analysis of market trends and emerging opportunities at AFFF Private Executive Film Investment Roundtable",
      "Become an active member of committees shaping the future of film finance at AFFF Private Executive Film Investment Roundtable",
      "Enjoy a premium networking environment with top-tier investors and industry leaders at AFFF Investors Lounge",
      "Complimentary tea and coffee breaks at the AFFF Investors Lounge",
      "Complimentary lunch at the AFFF Investors Lounge",
      "Access bespoke investment opportunities aligned with your strategic goals at the AFFF Investors Room",
      "Benefit from matchmaking services to connect with AFFF accredited seasoned financiers and investors at AFFF Lounge",
      "Experience engaging and diverse entertainment throughout the forum",
      "Access Insights on Maximizing Your ROI in Film Financing Forum session",
      "Participate in VIP think tanks and brainstorming sessions with industry leaders from the film and finance industry at the Forum",
      "Access to insight of potential investment avenues and funding opportunities for your film projects at AFFF Finance Forum",
      "Enjoy best seat reservation at the AFFF Film Financing Forum",
      "Learn how to collaborate with other sectors for film financing at AFFF industry integration session",
      "Gain access to Industry leaders from other sector such as sustainability, health, military, tourism etc. for film collaboration at AFFF industry integration session",
      "Access to extensive resources and learning materials designed to expand your knowledge and expertise of the film financing at AFFF Master Class",
      "Benefit from expert-led sessions and class that enhance your investment strategies and knowledge at the AFFF Master Class",
      "Get certification from a premium film academy at the AFFF Master Class",
      "Stand a chance to compete in the AFFF Pitch Competition",
      "Attend AFFF private dinners and cocktails designed for deep, impactful networking",
      "Witness cutting-edge demonstrations of revolutionary film technologies and services at the AFFF Exhibition/Showcase",
      "Discover new film sites and collaborate with tourist sites at AFFF CineTour for your next film project",
      "Access to AFFF Film Premier",
      "Enjoy the recognition and prestige associated with elite status of Premium Elite Plus Film Industry professional at the AFFF event",
      "Benefit from a reserved VIP parking spot at the AFFF venue for unparalleled convenience and ease",
      "Benefit from enhanced media exposure and promotional opportunities at AFFF event",
      "Enjoy the opportunity to book meetings with AFFF key stakeholders and attendees",
      "Engage with key stakeholders and thought leaders in a high-impact forum at the AFFF event",
      "Win exclusive prizes through our thrilling raffles at AFFF",
      "Receive a custom event itinerary based on your interests and goals",
      "Attend invitation-only receptions with key stakeholders at AFFF",
      "Amplify your presence and influence among top finance industry professionals at AFFF",
      "Dedicated VIP check-in at AFFF"
    ]
  };

  const toggleFeatures = () => {
    setExpandedFeatures(prev => ({
      ...prev,
      [pitchBadge.name]: !prev[pitchBadge.name]
    }));
  };

  const handleGetBadge = () => {
    // Add to cart using the context, including the reference code
    addToCart({
      name: pitchBadge.name,
      price: pitchBadge.price,
      details: pitchBadge.details,
      type: 'badge',
      referenceCode: referenceCode // Pass the reference code
    });
    // No need to navigate - the cart drawer will open automatically
  };

  // Navigate to home page
  const goToHome = () => {
    navigate('/');
  };

  const isExpanded = expandedFeatures[pitchBadge.name];
  const displayedFeatures = isExpanded 
    ? pitchBadge.features 
    : pitchBadge.features.slice(0, 8);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Home Icon Button - Fixed at top left with more margin */}
      <div className="absolute top-6 left-6 z-10">
        <button 
          onClick={goToHome}
          className="bg-custom-red text-white p-3 rounded-full shadow-lg hover:bg-red-700 transition-all duration-300 flex items-center justify-center"
          aria-label="Back to Home"
        >
          <FaHome size={24} />
        </button>
      </div>

      {/* Reference code display if available */}
      {referenceCode && (
        <div className="max-w-7xl mx-auto mb-4">
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Your reference code:</span> {referenceCode}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              This code will be automatically applied to your badge purchase.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-lexend font-bold text-gray-900 mb-4">
            Complete Your Pitch Registration
          </h1>
          <p className="text-lg font-inter text-gray-600">
            Get your mandatory Premium Elite Plus Badge to participate in the AFFF Pitch Competition
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <motion.div
            className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transition-all ring-2 ring-custom-red"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="p-6">
              <h3 className="text-xl font-lexend font-bold text-gray-900 mb-2">
                {pitchBadge.name}
              </h3>
              
              <div className="mb-4">
                <span className="text-2xl font-lexend font-bold text-custom-red">
                  {formatPrice(pitchBadge.price)}
                </span>
                <span className="font-inter text-gray-600 ml-2">{pitchBadge.details}</span>
              </div>

              <div className="bg-yellow-50 p-3 rounded-md mb-4 border-l-4 border-yellow-400">
                <p className="text-sm text-yellow-800 font-medium">
                  This badge is mandatory for all pitch participants and gives you access to present your project 
                  at the AFFF Pitch Competition.
                </p>
              </div>

              <div className="bg-blue-50 p-3 rounded-md mb-4">
                <h4 className="font-lexend font-semibold text-sm mb-2">Access to:</h4>
                <ul className="font-inter text-sm space-y-1">
                  {pitchBadge.summary.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-4">
                <h4 className="font-lexend font-semibold text-sm mb-2">Features:</h4>
                <ul className="font-inter text-sm space-y-2">
                  {displayedFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-custom-red mr-2">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {pitchBadge.features.length > 8 && (
                  <button 
                    className="font-lexend text-custom-red font-semibold text-sm mt-3 hover:underline"
                    onClick={toggleFeatures}
                  >
                    {isExpanded ? "Show Less" : "Show More"}
                  </button>
                )}
              </div>
              
              <button 
                className="w-full py-2 px-4 rounded-md font-lexend font-semibold text-sm mt-4 bg-custom-red text-white hover:bg-red-700"
                onClick={handleGetBadge}
              >
                Get Badge
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PitchBadgeSelection; 